import { useState, useEffect, useCallback } from 'react';
import { Prefecture, City, getCachedAddressData, getCitiesByPrefectureCode } from '../data/locations';

export interface UseAddressDataResult {
  prefectures: Prefecture[];
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

export interface UseLocationSelectorResult {
  prefectures: Prefecture[];
  selectedPrefecture: Prefecture | null;
  selectedCity: City | null;
  cities: City[];
  isLoading: boolean;
  error: string | null;
  setSelectedPrefectureCode: (code: string | null) => void;
  setSelectedCityCode: (code: string | null) => void;
  resetSelection: () => void;
  getLocationString: () => string;
}

// 基本的な住所データ取得フック
export const useAddressData = (s3BaseUrl?: string): UseAddressDataResult => {
  const [prefectures, setPrefectures] = useState<Prefecture[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getCachedAddressData(s3BaseUrl);
      setPrefectures(data);
    } catch (err) {
      console.error('住所データの読み込みに失敗しました:', err);
      setError(err instanceof Error ? err.message : '住所データの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [s3BaseUrl]);

  const refreshData = useCallback(async () => {
    await loadData();
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    prefectures,
    isLoading,
    error,
    refreshData
  };
};

// 都道府県・市区町村選択フック
export const useLocationSelector = (s3BaseUrl?: string): UseLocationSelectorResult => {
  const { prefectures, isLoading, error, refreshData } = useAddressData(s3BaseUrl);
  const [selectedPrefectureCode, setSelectedPrefectureCode] = useState<string | null>(null);
  const [selectedCityCode, setSelectedCityCode] = useState<string | null>(null);

  // 選択された都道府県の取得
  const selectedPrefecture = selectedPrefectureCode 
    ? prefectures.find(p => p.code === selectedPrefectureCode) || null
    : null;

  // 都道府県に対応する市区町村リストの取得
  const cities = selectedPrefectureCode 
    ? getCitiesByPrefectureCode(prefectures, selectedPrefectureCode)
    : [];

  // 選択された市区町村の取得
  const selectedCity = selectedCityCode && cities.length > 0
    ? cities.find(c => c.code === selectedCityCode) || null
    : null;

  // 都道府県選択時の処理
  const handleSetSelectedPrefectureCode = useCallback((code: string | null) => {
    setSelectedPrefectureCode(code);
    // 都道府県を変更した場合、市区町村の選択をリセット
    setSelectedCityCode(null);
  }, []);

  // 市区町村選択時の処理
  const handleSetSelectedCityCode = useCallback((code: string | null) => {
    setSelectedCityCode(code);
  }, []);

  // 選択のリセット
  const resetSelection = useCallback(() => {
    setSelectedPrefectureCode(null);
    setSelectedCityCode(null);
  }, []);

  // 選択中の地域の文字列表現を取得
  const getLocationString = useCallback(() => {
    if (!selectedPrefecture) return '';
    if (!selectedCity) return selectedPrefecture.name;
    return `${selectedPrefecture.name} ${selectedCity.name}`;
  }, [selectedPrefecture, selectedCity]);

  return {
    prefectures,
    selectedPrefecture,
    selectedCity,
    cities,
    isLoading,
    error,
    setSelectedPrefectureCode: handleSetSelectedPrefectureCode,
    setSelectedCityCode: handleSetSelectedCityCode,
    resetSelection,
    getLocationString
  };
};

// 郵便番号から住所情報を検索するフック
export const usePostalCodeSearch = () => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const searchByPostalCode = useCallback(async (postalCode: string): Promise<{
    prefecture: string;
    city: string;
    address: string;
  } | null> => {
    // 郵便番号の形式チェック（ハイフンありなしどちらも対応）
    const cleanedCode = postalCode.replace(/[-\s]/g, '');
    if (!cleanedCode || !/^\d{7}$/.test(cleanedCode)) {
      setSearchError('正しい郵便番号を入力してください（例：1000001 または 100-0001）');
      return null;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      // バックエンドのZipcloud APIを使用
      const { authApi } = await import('../services/api');
      const response = await authApi.searchAddress(cleanedCode);
      
      if (response.addresses && response.addresses.length > 0) {
        const address = response.addresses[0];
        console.log('Postal code search result:', address);
        
        return {
          prefecture: address.prefecture,
          city: address.city,
          address: address.town || ''
        };
      } else {
        throw new Error('該当する郵便番号が見つかりませんでした');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '郵便番号の検索に失敗しました';
      console.error('Postal code search error:', error);
      setSearchError(message);
      return null;
    } finally {
      setIsSearching(false);
    }
  }, []);

  return {
    searchByPostalCode,
    isSearching,
    searchError
  };
};

// デバッグ用: データの統計情報を取得
export const useAddressDataStats = (prefectures: Prefecture[]) => {
  return {
    prefectureCount: prefectures.length,
    totalCityCount: prefectures.reduce((total, pref) => total + pref.cities.length, 0),
    cityCountByPrefecture: prefectures.map(pref => ({
      prefecture: pref.name,
      cityCount: pref.cities.length
    }))
  };
};
